/**
 * Test Mode tab types (TM-8).
 *
 * Source of truth for the row shape is pom-2-api's
 * `TestModeReportService.getTestStatusList` (docs/test-mode/test-mode-contract.md).
 * SQL returns 0/1 for booleans; normalize with `Boolean()` at the render edge.
 */

/** Row returned by GET /v1/test-mode/status-list. SQL booleans arrive as 0/1. */
export interface TestModeStatusRow {
    userId: string;
    name: string;
    team: string | null;
    group: string | null;
    inTest: 0 | 1;
    testedApp: 0 | 1;
    testedDevice: 0 | 1;
    devicePaired: 0 | 1;
    exitedWithoutTest: 0 | 1;
    pairedDeviceUntested: 0 | 1;
    expiresAt: string | null;
    lastEnteredAt: string | null;

    // ---- Still missing from the backend (open with Evan). ----
    /** Needs a warehouse Users identifier column. */
    employeeId?: string | null;
    /** Needs a storage decision for request-exit state. */
    exitRequested?: boolean;
}

/** PaginationFactory envelope pom-2-api wraps list results in. */
export interface TestModeStatusPage {
    items: TestModeStatusRow[];
    page: number;
    perPage: number;
    total: number;
}

/** Request body for POST /v1/test-mode/re-enroll. */
export interface ReEnrollRequest {
    userIds: string[];
    windowType: 'onboarding' | 'self-test';
}

/** Request body for POST /v1/test-mode/request-exit (bulk CTA). */
export interface RequestExitRequest {
    userIds: string[];
}

/**
 * Row returned by GET /v1/test-mode/report ("who tested", warehouse-backed).
 * NOTE: "attended training" is intentionally absent — no data source exists
 * for MVP (see test-mode-pom-2-api.md open questions).
 */
export interface WhoTestedRow {
    userId: string;
    name: string;
    team: string | null;
    registered: 0 | 1;
    paired: 0 | 1;
    testedApp: 0 | 1;
    testedDevice: 0 | 1;
    sessionCount: number;
    lastEnteredAt: string | null;
    /** Not in the backend response yet (same gap as the status list). */
    group?: string | null;
}

export interface ReportParams {
    period: '7d' | '30d' | '90d';
    groupId?: string;
    teamId?: string;
}

export type TestModeKpiKey = 'inTestMode' | 'completed' | 'notTested' | 'flagged';

export interface TestModeKpiCounts {
    inTestMode?: number;
    completed?: number;
    notTested?: number;
    /** null = backend cannot answer this yet (no flag fields in the warehouse). */
    flagged?: number | null;
}

export interface StatusListParams {
    status?: 'in-test' | 'not-in-test' | 'flagged' | 'all';
    testCompleted?: boolean;
    device?: 'none' | 'paired-untested' | 'paired-tested';
    name?: string;
    groupId?: string;
    teamId?: string;
    page: number;
    perPage: number;
}

/** The filter bar's applied values ('' = not filtering). */
export interface TestModeFilterValues {
    status: '' | 'in-test' | 'live' | 'flagged';
    appTest: '' | 'yes' | 'no';
    device: '' | 'none' | 'paired-untested' | 'paired-tested';
    name: string;
    groupId: string;
    teamId: string;
}

export const EMPTY_FILTER_VALUES: TestModeFilterValues = {
    status: '', appTest: '', device: '', name: '', groupId: '', teamId: '',
};

/** Map applied filter-bar values onto status-list query params. */
export const filtersToParams = (f: TestModeFilterValues): Partial<StatusListParams> => ({
    ...(f.status === 'in-test' ? { status: 'in-test' as const } : {}),
    ...(f.status === 'live' ? { status: 'not-in-test' as const } : {}),
    ...(f.status === 'flagged' ? { status: 'flagged' as const } : {}),
    ...(f.appTest === 'yes' ? { testCompleted: true } : {}),
    ...(f.appTest === 'no' ? { testCompleted: false } : {}),
    ...(f.device ? { device: f.device } : {}),
    ...(f.name ? { name: f.name } : {}),
    ...(f.groupId ? { groupId: f.groupId } : {}),
    ...(f.teamId ? { teamId: f.teamId } : {}),
});

export const hasAnyFilter = (f: TestModeFilterValues): boolean =>
    Object.values(f).some((v) => v !== '');

/** Derived row status driving the Status chip and the Flagged cohort. */
export type RowStatus = 'test' | 'live' | 'flagged';

export const deriveRowStatus = (row: TestModeStatusRow): RowStatus => {
    if (row.exitedWithoutTest || row.pairedDeviceUntested) return 'flagged';
    return row.inTest ? 'test' : 'live';
};
